import { Constants, FetchXmlBuilder, FetchXmlParser } from "../../../fetch-xml";
import { FetchXmlFactory } from "../../../fetch-xml/FetchXmlFactory";
import { Attribute, DataType, DataTypes } from "../../../metadata";
import { Record } from "../Record/Record";
import { Type as FilterType, Operators } from '../../../fetch-xml/filter';
import { DataProvider } from "../DataProvider";
import { PromiseCache } from "../../../../helpers";
import { IColumn } from "../../interfaces";
import { IDataProvider, ILayout, IRecord } from "../interfaces";
import { RecordSaveHandler } from "./SaveHandler";
import { DeepPartial } from "../interfaces";
import deepEqual from "deep-eql";

const currencyCache = new PromiseCache();
const titleCache = new PromiseCache();
const layoutJSONCache = new PromiseCache();

/**
 * Interface representing the configuration of a column 
 * for the FetchXML Data Provider.
 */
export interface IFetchXmlDataProviderColumn extends DeepPartial<IColumn> {
    name: string
}

/**
 * DataProviderOptions for FetchXmlDataProvider.
 */
export interface IFetchXmlDataProviderOptions {
    /**
     * Whether the provided should attempt to fetch layoutJSON. Set this to true if you are only using the provider for data fetching (eg. it's not being used for any Dataset control).
     */
    doNotFetchLayout?: boolean;
    /**
     * Whether the provided should attempt to fetch view title. Set this to true if you are only using the provider for data fetching (eg. it's not being used for any Dataset control).
     */
    doNotFetchTitle?: boolean;
}
/**
 * Provides functionality to use fetchXml as a data source.
 */
export class FetchXmlDataProvider extends DataProvider implements IDataProvider {
    private _error: boolean = false;
    private _errorMessage: string = "";
    private _columns: IFetchXmlDataProviderColumn[] = [];
    private _layoutColumns: IFetchXmlDataProviderColumn[] = [];
    private _doNotFetchLayout: boolean = false;
    private _doNotFetchTitle: boolean = false;
    //@ts-ignore - gets defined on first init
    private _entityMetadata: Xrm.Metadata.EntityMetadata;
    private _pageNumber: number = 1;
    private _pageSize: number = Constants.DEFAULT_PAGE_SIZE_SUBGRID;
    private _entityName: string = "";
    private _viewId: string = "";
    private _totalRecordCount: number = 0;
    private _fetchXmlObject: FetchXmlBuilder.fetch | null = null;
    private _hasBeenInitialized: boolean = false;
    private _entityRecords: any[] = [];
    private _pagingCookie: string | null = null;
    private _haveColumnsChangedFromLastDataRetrieval: boolean = false;
    private _linkedEntitiesMetadata = new Map<string, Xrm.Metadata.EntityMetadata>();
    private _title: string = "";
    private _isUserQuery: boolean = false;
    private _userQueryTableName = 'userquery';
    /**
     * Allows yout to use fetchXml as Data Source.
     */
    constructor(fetchXml: string, options?: IFetchXmlDataProviderOptions) {
        super();
        this._doNotFetchLayout = !!options?.doNotFetchLayout;
        this._doNotFetchTitle = !!options?.doNotFetchTitle;
        //@ts-ignore - typings
        if (window.TALXIS?.Portal) {
            this._userQueryTableName = 'talxis_userquery';
        }
        if (!fetchXml) {
            return;
        }
        //we need to get as many stuff done synchronously in constructor => we might need to access it before first refresh
        this._fetchXmlObject = this._parseFetchXml(fetchXml);
    }
    public isError() {
        return this._error;
    }

    public getTitle(): string {
        return this._title;
    }

    public setTitle(title: string) {
        this._title = title;
    }
    public getErrorMessage(): string {
        return this._errorMessage;
    }
    public getMetadata() {
        return this._entityMetadata;
    }
    public setMetadata(metadata: DeepPartial<Xrm.Metadata.EntityMetadata>) {
        //@ts-ignore - can be partial as rest the entityMetadata will be fetched on init
        this._entityMetadata = metadata;
    }
    public setLayoutColumns(columns: IFetchXmlDataProviderColumn[]) {
        this._layoutColumns = columns;
    }
    public getEntityName() {
        return this._entityName;
    }
    public setEntityName(entityName: string) {
        this._entityName = entityName;
    }
    public getColumns() {
        //we need to return empty columns as they might not be fully loaded at this point
        if (!this._hasBeenInitialized) {
            return [];
        }
        return this._columns as IColumn[];
    }

    public setColumns(columns: IFetchXmlDataProviderColumn[]) {
        if (!deepEqual(columns, this._columns)) {
            this._columns = columns;
            this._haveColumnsChangedFromLastDataRetrieval = true;
        }
    }

    public setDataSource(fetchXml: string): void {
        if (fetchXml != this._fetchXmlObject?.toXml()) {
            this._fetchXmlObject = this._parseFetchXml(fetchXml);
            this._columns = [];
            //@ts-ignore - gets defined on init
            this._entityMetadata = {};
            this._layoutColumns = [];
            this.setSorting([]);
            this.setFiltering(null);
            this.setLinking([]);
            this.setSearchQuery(undefined);
            this._hasBeenInitialized = false;
        }
    }
    public getDataSource() {
        return this._fetchXmlObject?.toXml();
    }

    public async refresh(): Promise<IRecord[]> {
        this._pageNumber = 1;
        this._pagingCookie = null;
        await this._init();
        await this._fetchRecords();
        return this.getRecords();
    }

    public getRecords(): IRecord[] {
        return this._entityRecords.map((record: any) => {
            const recordId = record[this._entityMetadata.PrimaryIdAttribute];
            return new Record(recordId, this);
        });
    }
    public getPaging() {
        return {
            firstPageNumber: this._pageNumber,
            hasNextPage: !!this._pagingCookie,
            hasPreviousPage: this._pageNumber > 1,
            lastPageNumber: this._pageNumber,
            pageSize: this._pageSize,
            pageNumber: this._pageNumber,
            loadExactPage: async (pageNumber: number) => {
                await this._init();
                if (pageNumber !== this._pageNumber + 1) {
                    this._pagingCookie = null;
                }
                this._pageNumber = pageNumber;
                await this._fetchRecords(true);
                return this.getRecords();
            },
            loadNextPage: async () => {
                this._pageNumber++;
                await this._fetchRecords(true);
                return this.getRecords();
            },
            loadPreviousPage: async () => {
                this._pageNumber--;
                this._pagingCookie = null;
                await this._fetchRecords();
                return this.getRecords();
            },
            reset: () => this.refresh(),
            setPageSize: (pageSize: number) => {
                this._pageSize = pageSize;
            },
            totalResultCount: this._totalRecordCount
        };
    }

    public async save(record: IRecord) {
        return new RecordSaveHandler(record, this).save();
    }

    public getViewId(): string {
        return this._viewId;
    }

    public getRawRecordData(recordId: string): { [key: string]: any; } | null {
        return this._entityRecords.find(x => x[this._entityMetadata!.PrimaryIdAttribute] === recordId) ?? null;
    }

    public openDatasetItem(entityReference: ComponentFramework.EntityReference): void {
        window.Xrm.Navigation.navigateTo({
            pageType: 'entityrecord',
            entityName: entityReference.etn!,
            entityId: entityReference.id.guid,
        });
    }
    private async _init() {
        try {
            this._error = false;
            this._errorMessage = "";
            const promises: Promise<any>[] = [];
            if (!this._fetchXmlObject) {
                return;
            }
            //if we did not set title from outside and we have viewId, fetch the title
            if (!this._doNotFetchTitle && this._viewId) {
                promises.push(this._setTitle(this._viewId));
            }
            //if we did not set currencies from outside and there is a currency column, fetch the currencies
            if (this.getCurrencies().length === 0 && this.getColumns().find(x => x.dataType === DataTypes.Currency)) {
                promises.push(this._setEnvironmentCurrencies());
            }
            //load layout columns, only if no columns are present
            if (this._viewId && !this._doNotFetchLayout && this._columns.length === 0) {
                await this._setLayoutXmlColumns(this._viewId);
            }
            if (this._columns.length === 0) {
                this._layoutColumns.map(layoutCol => this._columns.push(layoutCol));
            }
            if (!this._hasBeenInitialized || this._haveColumnsChangedFromLastDataRetrieval) {
                const parsedFetchXml = await new FetchXmlParser().parse(this._fetchXmlObject.toXml(), this._columns, this._hasBeenInitialized);
                this._columns = parsedFetchXml.columns;
                //this is so we are able to load sorting and linking from state on init if it has been set
                if (!this._hasBeenInitialized) {
                    if (this.getSorting().length === 0) {
                        this.setSorting(parsedFetchXml.sortStatus);
                    }
                    if (this.getLinking().length === 0) {
                        this.setLinking(parsedFetchXml.linkedEntities);
                    }
                }
                else {
                    this.setSorting(parsedFetchXml.sortStatus);
                    this.setLinking(parsedFetchXml.linkedEntities);
                }
            }
            await Promise.all(promises);
            this._hasBeenInitialized = true;
            this._haveColumnsChangedFromLastDataRetrieval = false;
        }
        catch (err) {
            this._error = true;
            this._errorMessage = err as string;
            throw err;
        }
    }

    private async _setTitle(queryId: string) {
        this._title = await titleCache.get(queryId, async () => {
            const result = await window.Xrm.WebApi.retrieveRecord(this._isUserQuery ? this._userQueryTableName : 'savedquery', queryId, '?$select=name');
            return result.name;
        });
    }

    private async _fetchRecords(includePaginationCookie?: boolean) {
        if (!this._fetchXmlObject) {
            return;
        }
        await this._fetchEntityMetadata();
        const fetchXml = this._buildFetchXml(this._fetchXmlObject, includePaginationCookie);
        let options = `?fetchXml=${encodeURIComponent(fetchXml)}&$count=true`;
        const result = await window.Xrm.WebApi.retrieveMultipleRecords(this._entityName, options);
        //@ts-ignore - typings
        this._pagingCookie = result.fetchXmlPagingCookie;
        this._entityRecords = result.entities;
        //@ts-ignore - typings
        this._totalRecordCount = result._totalRecordCount;
        this.setLoading(false);
    }

    private async _fetchEntityMetadata() {
        const nonLinkedEntityColumns = this.getColumns().filter(x => !x.name.includes('.'));
        this._entityMetadata = { ...this._invokeGetters(await window.Xrm.Utility.getEntityMetadata(this._entityName, nonLinkedEntityColumns.map(x => x.name))), ...this._entityMetadata };
        nonLinkedEntityColumns.map(col => {
            let attributeMetadata: any = this._entityMetadata?.Attributes?.get(col.name);
            col.metadata = { ...attributeMetadata, ...attributeMetadata?.attributeDescriptor, ...col.metadata };
        });

        const linkedEntityColumns = this.getColumns().filter(x => x.name.includes('.'));
        return Promise.all(linkedEntityColumns.map(async col => {
            const attributeName = Attribute.GetNameFromAlias(col.name);
            const linkedEntity = this.getLinking().find(x => x.alias === Attribute.GetLinkedEntityAlias(col.name))!;
            this._linkedEntitiesMetadata.set(linkedEntity.name, await window.Xrm.Utility.getEntityMetadata(linkedEntity.name, [attributeName]));
            let attributeMetadata: any = this._linkedEntitiesMetadata.get(linkedEntity.name)?.Attributes.get(attributeName);
            col.metadata = { ...this._invokeGetters(attributeMetadata), ...this._invokeGetters(attributeMetadata?.attributeDescriptor), ...col.metadata };
        }));
    }
    private _buildFetchXml(fetchXmlObject: FetchXmlBuilder.fetch, includePaginationCookie?: boolean) {
        return new FetchXmlFactory(fetchXmlObject.toXml())
            .setLinking(this.getLinking())
            .setColumns(this.getColumns())
            .setSorting(this.getSorting())
            .setFilter(this._getFilters())
            .setPageSize(this.getPaging().pageSize)
            .setPageNumber(this.getPaging().pageNumber, includePaginationCookie ? this._pagingCookie ?? "" : "")
            .build();
    }

    private _getFilters() {
        const filters: ComponentFramework.PropertyHelper.DataSetApi.FilterExpression[] = [];
        if (this.getFiltering()) {
            filters.push(this.getFiltering()!);
        }
        const searchQueryFilter = this._getSearchQueryFilter();
        if (searchQueryFilter) {
            filters.push(searchQueryFilter);
        }
        return filters;
    }

    private _getSearchQueryFilter(): ComponentFramework.PropertyHelper.DataSetApi.FilterExpression | null {
        if (!this.getSearchQuery()) {
            return null;
        }
        return {
            filterOperator: FilterType.Or.Value,
            conditions: this.getColumns().filter(x => {
                //ignore guid for fulltext search
                if (this._entityMetadata?.PrimaryIdAttribute === x.name) {
                    return false;
                }
                //ignore virtual columns
                if (x.name.endsWith('__virtual')) {
                    return false;
                }
                switch (x.dataType) {
                    case DataTypes.Currency:
                    case DataTypes.DateAndTimeDateAndTime:
                    case DataTypes.DateAndTimeDateOnly:
                    case DataTypes.Decimal:
                    case DataTypes.File:
                    case DataTypes.Fp:
                    case DataTypes.Image:
                    case DataTypes.WholeDuration:
                    case DataTypes.WholeLanguage:
                    case DataTypes.WholeNone:
                    case DataTypes.WholeTimeZone: {
                        //these column types are not supported for full text search
                        //numbers and dates could possibly be includes TODO:
                        return false;
                    }
                }
                return true;
            }).map(col => {
                const entityAliasName = Attribute.GetLinkedEntityAlias(col.name);
                const attributeName = Attribute.GetNameFromAlias(col.name);
                return {
                    attributeName: this._attributeNameDecorator(col.dataType as DataType, attributeName),
                    conditionOperator: Operators.Like.Value,
                    value: `%${this.getSearchQuery()}%`,
                    entityAliasName: entityAliasName ?? undefined
                };
            })
        };
    }

    //TODO: these decorators/undecorators could be shared
    private _attributeNameDecorator(dataType: DataType, attributeName: string) {
        switch (dataType) {
            case DataTypes.OptionSet:
            case DataTypes.TwoOptions:
            case DataTypes.LookupOwner:
            case DataTypes.LookupSimple:
            case DataTypes.LookupCustomer: {
                if (attributeName.endsWith('name')) {
                    return attributeName;
                }
                return `${attributeName}name`;
            }
            default: {
                return attributeName;
            }
        }
    }
    private async _setEnvironmentCurrencies() {
        const currencies = await currencyCache.get('currencies', async () => {
            const result = await window.Xrm.WebApi.retrieveMultipleRecords('transactioncurrency', '?$select=currencysymbol,transactioncurrencyid,currencyname');
            return result.entities;
        });
        this.setCurrencies(currencies);
    }

    private _invokeGetters(obj: any) {
        const result: any = {};
        const keys = Object.getOwnPropertyNames(Object.getPrototypeOf(obj ?? {}));
        if (keys.includes('__proto__')) {
            //passed object is not an instance of class => getters will be called automatically with spread operator
            return obj;
        }
        for (const key of keys) {
            if (key === 'constructor') {
                continue;
            }
            try {
                result[key] = obj[key];
            }
            catch (err) {
            }
        }
        return result;
    }

    private _parseFetchXml(fetchXml: string) {
        const fetchXmlObject = FetchXmlBuilder.fetch.fromXml(fetchXml);
        this._pageSize = fetchXmlObject.count || Constants.DEFAULT_PAGE_SIZE_SUBGRID;
        this._entityName = fetchXmlObject.entity.name ?? this._entityName;
        this._pageNumber = fetchXmlObject.page || 1;
        this._viewId = fetchXmlObject.savedqueryid ?? fetchXmlObject.userqueryid ?? "";
        if (fetchXmlObject.userqueryid) {
            this._isUserQuery = true;
        }
        else {
            this._isUserQuery = false;
        }
        return fetchXmlObject;
    }
    private async _setLayoutXmlColumns(viewId: string) {
        this._layoutColumns = await layoutJSONCache.get(viewId, async () => {
            const response = await window.Xrm.WebApi.retrieveRecord(this._isUserQuery ? 'userquery' : 'savedquery', viewId, '?$select=layoutjson');
            const layout: ILayout = JSON.parse(response.layoutjson);
            return layout.Rows[0].Cells.map(x => {
                return {
                    name: x.Name,
                    isHidden: x.IsHidden,
                    visualSizeFactor: x.Width,
                };
            });
        });
    }

    public static GetParsedData(data: string | null): string | null {
        if (!data) {
            return data;
        }
        try {
            FetchXmlBuilder.fetch.fromXml(data);
            return data;
        }
        catch (err) {
            return null;
        }
    }
}